import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail, getActiveHospitalId, setActiveHospitalId } from "@/lib/api";
import {
  House, UsersThree, Stethoscope, Bed, ChartBar, Buildings,
  IdentificationCard, Gear, SignOut, Hospital, Sparkle, ListChecks, Books, Palette, Key
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const sections = [
  { group: "Workspace", items: [
    { to: "/", label: "Dashboard", icon: House },
  ]},
  { group: "Patient flow", items: [
    { to: "/patients", label: "Patients", icon: IdentificationCard },
    { to: "/opd", label: "OPD Visits", icon: Stethoscope },
    { to: "/ipd", label: "IPD Admissions", icon: Bed },
    { to: "/masters", label: "Masters", icon: Books },
  ]},
  { group: "Operations", id: "operations", items: [
    { to: "/reports", label: "Reports", icon: ChartBar, id: "reports" },
  ]},
  { group: "Administration", roles: ["admin", "superuser"], items: [
    { to: "/staff", label: "Staff", icon: UsersThree, roles: ["admin", "superuser"] },
    { to: "/custom-fields", label: "Custom Fields", icon: ListChecks, roles: ["admin", "superuser"] },
    { to: "/settings", label: "Hospital Settings", icon: Gear },
    { to: "/hospitals", label: "All Hospitals", icon: Buildings, roles: ["superuser"] },
    { to: "/branding", label: "Login Branding", icon: Palette, roles: ["superuser"] },
  ]},
];

function RoleBadge({ role }) {
  // Intentionally suppressed — role pill removed from the top bar per product
  // request. Kept as no-op for any callers.
  void role;
  return null;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [hospitals, setHospitals] = useState([]);
  const [activeHid, setActiveHid] = useState(getActiveHospitalId());
  const [brand, setBrand] = useState({ app_name: "MEDIRA", app_tagline: "HMS · IPD/OPD", logo_url: "" });
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    if (user?.role === "superuser") {
      api.get("/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    api.get("/branding").then((r) => {
      if (r.data) setBrand((b) => ({ ...b, ...r.data }));
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const handleHospitalChange = (hid) => {
    setActiveHospitalId(hid);
    setActiveHid(hid);
    // Hard reload so every page refetches under the new scope.
    window.location.reload();
  };

  // Per-hospital toggle to hide the Reports tab from `user` role.
  const reportsHiddenForUser =
    user?.role === "user" && user?.hospital?.reports_enabled_for_users === false;

  // Per-hospital superuser-controlled flag to hide the whole OPD module.
  const opdHidden = user?.hospital?.opd_module_enabled === false;

  return (
    <div className="min-h-screen flex" style={{ background: "var(--hms-app)" }}>
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:w-64 flex-col text-white sticky top-0 h-screen no-print"
        style={{ background: "var(--hms-sidebar)" }}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2">
          <div className="h-9 w-9 rounded-sm overflow-hidden grid place-items-center" style={{ background: "rgba(255,255,255,0.08)" }}>
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="logo" className="h-full w-full object-cover" data-testid="sidebar-logo" />
            ) : (
              <Hospital size={22} weight="fill" className="text-[var(--hms-accent)]" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-lg leading-none tracking-tight truncate" data-testid="sidebar-app-name">{brand.app_name || "MEDIRA"}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-1 truncate" data-testid="sidebar-app-tagline">{brand.app_tagline || ""}</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {sections.map((s) => {
            if (s.roles && !s.roles.includes(user?.role)) return null;
            // Hide whole "Operations" group if Reports is the only child and it's hidden for this user.
            const visibleItems = s.items.filter((it) => {
              if (it.roles && !it.roles.includes(user?.role)) return false;
              if (it.id === "reports" && reportsHiddenForUser) return false;
              if (it.to === "/opd" && opdHidden) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;
            return (
              <div key={s.group} className="mb-4">
                <div className="px-5 mb-1 text-[10px] uppercase tracking-[0.16em] font-bold text-white/40 font-heading">
                  {s.group}
                </div>
                {visibleItems.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.to === "/"}
                      data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                          isActive
                            ? "bg-white/10 border-l-4 border-[var(--hms-accent)] text-white"
                            : "border-l-4 border-transparent text-white/70 hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      <Icon size={18} weight={"regular"} />
                      <span className="font-medium">{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-9 w-9 rounded-sm bg-white/10 flex items-center justify-center font-heading font-bold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name || "User"}</div>
              <div className="text-[11px] text-white/50 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPwOpen(true)}
            data-testid="change-password-btn"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 rounded-sm mb-1"
          >
            <Key size={16} weight="bold" className="mr-2" />
            Change password
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 rounded-sm"
          >
            <SignOut size={16} weight="bold" className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-[var(--hms-border)] no-print">
          <div className="h-14 flex items-center justify-between px-6">
            <div className="flex items-center gap-3 min-w-0">
              <Sparkle size={16} weight="fill" className="text-[var(--hms-accent)]" />
              <div className="font-heading font-bold text-sm uppercase tracking-[0.14em] truncate">
                {user?.hospital?.name || "Multi-Hospital Workspace"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user?.role === "superuser" && (
                <div className="flex items-center gap-2" data-testid="superuser-hospital-scope">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--hms-muted)]">
                    Viewing
                  </span>
                  <select
                    value={activeHid}
                    onChange={(e) => handleHospitalChange(e.target.value)}
                    data-testid="superuser-hospital-select"
                    className="px-2.5 py-1.5 rounded-sm border border-[var(--hms-border-2)] bg-white text-xs font-medium hover:border-[var(--hms-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--hms-accent)]/40 max-w-[180px]"
                    title="Scope all data (patients, OPD, IPD, masters, staff, reports) to one hospital"
                  >
                    <option value="">All hospitals (combined)</option>
                    {hospitals.map((h) => (
                      <option key={h.id} value={h.id}>{h.name} · {h.code}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="hidden md:flex flex-col items-end leading-tight">
                <div className="text-xs font-semibold">{user?.name}</div>
                <div className="text-[10px] text-[var(--hms-muted)]">{user?.email}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="px-6 py-6">
            <Outlet />
          </div>
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/users/me/password", { current_password: current, new_password: next });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Change password</DialogTitle>
          <DialogDescription className="text-xs text-[var(--hms-text-2)]">
            Enter your current password and choose a new one. Minimum 6 characters.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Current password</Label>
            <Input type="password" autoFocus required value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current"
              className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">New password</Label>
            <Input type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="cp-new"
              className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
            <p className="text-[10px] text-[var(--hms-muted)] mt-1">Minimum 6 characters.</p>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Confirm new password</Label>
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="cp-confirm"
              className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
              className="rounded-sm border-[var(--hms-border-2)] bg-[var(--hms-paper)]" data-testid="cp-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="cp-submit"
              className="rounded-sm bg-[var(--hms-accent)] hover:bg-[var(--hms-accent)]/90">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
